<template>
    <div class="page">
        <div class="page-header">
            <h1>管理員管理</h1>
            <p>管理系統中的管理員帳號和權限</p>
        </div>

        <div class="content vaporwave-theme">
            <!-- 搜尋和新增按鈕 -->
            <div class="toolbar">
                <div class="filters">
                    <a-space>
                        <a-input
                            v-model:value="filters.name"
                            placeholder="名稱"
                            size="middle"
                            class="filter-input"
                        />
                        <a-input
                            v-model:value="filters.email"
                            placeholder="Email"
                            size="middle"
                            class="filter-input"
                        />
                        <a-select
                            v-model:value="filters.roleId"
                            placeholder="角色"
                            allow-clear
                            size="middle"
                            class="status-select min-w-[160px]"
                            :options="
                                allRoles.map((r) => ({
                                    label: r.name,
                                    value: r.id
                                }))
                            "
                        />
                        <a-button
                            size="middle"
                            class="unified-search-btn"
                            @click="handleSearch"
                        >
                            查詢
                        </a-button>
                        <a-button
                            size="middle"
                            class="unified-reset-btn"
                            @click="resetFilters"
                        >
                            重置
                        </a-button>
                    </a-space>
                </div>
                <a-button
                    v-if="canCreate"
                    type="primary"
                    class="unified-add-btn"
                    @click="showAddModal"
                >
                    新增管理員
                </a-button>
            </div>

            <!-- 管理員列表表格 -->
            <DataTable
                title=""
                :columns="columns"
                :data-source="managers"
                :pagination="pagination"
                @refresh="loadManagers"
                @change="handleTableChange"
                class="admin-table"
            >
                <template #bodyCell="{ column, record }">
                    <template v-if="column.key === 'name'">
                        <div class="user-info">
                            <span class="user-name">{{ record.name }}</span>
                        </div>
                    </template>
                    <template v-else-if="column.key === 'email'">
                        <span class="email-text">{{ record.email }}</span>
                    </template>
                    <template v-else-if="column.key === 'roles'">
                        <a-tag
                            v-for="role in resolveRoles(record)"
                            :key="
                                typeof role === 'object'
                                    ? role.id
                                    : String(role)
                            "
                            color="purple"
                            class="role-tag"
                            @click="showPermissions(record)"
                        >
                            {{ getRoleLabel(role) }}
                        </a-tag>
                    </template>
                    <template v-else-if="column.key === 'status'">
                        <template v-if="canEdit">
                            <a-switch
                                class="antdv-switch-clean"
                                :checked="isActiveStatus(record.status)"
                                size="small"
                                @change="
                                    (checked) => updateStatus(record, checked)
                                "
                            />
                        </template>
                        <template v-else>
                            <span class="status-text">
                                {{
                                    isActiveStatus(record.status)
                                        ? '啟用'
                                        : '停用'
                                }}
                            </span>
                        </template>
                    </template>
                    <template v-else-if="column.key === 'action'">
                        <a-space class="action-buttons">
                            <a-button
                                v-if="canEdit"
                                size="small"
                                class="action-btn-edit"
                                @click="editManager(record)"
                            >
                                編輯
                            </a-button>
                            <!-- 暫時隱藏角色管理功能 -->
                            <a-button
                                v-if="canAssignRoles"
                                size="small"
                                class="action-btn-assign"
                                @click="manageRoles(record)"
                            >
                                角色分配
                            </a-button>
                            <a-button
                                v-if="canDelete"
                                size="small"
                                class="action-btn-delete"
                                danger
                                @click="openDeleteManager(record)"
                            >
                                刪除
                            </a-button>
                        </a-space>
                    </template>
                </template>
            </DataTable>
        </div>

        <!-- 新增/編輯管理員 Modal -->
        <EditModal
            v-model:open="modalVisible"
            :title="modalMode === 'add' ? '新增管理員' : '編輯管理員'"
            :confirm-loading="modalLoading"
            :form-data="formState"
            :rules="modalMode === 'add' ? formRules : formRulesEdit"
            @ok="handleModalOk"
            @cancel="handleModalCancel"
        >
            <template #fields>
                <a-form-item label="名稱" name="name">
                    <a-input
                        v-model:value="formState.name"
                        placeholder="請輸入管理員名稱"
                    />
                </a-form-item>
                <a-form-item label="Email" name="email">
                    <a-input
                        v-model:value="formState.email"
                        placeholder="請輸入 Email"
                        :disabled="modalMode === 'edit'"
                    />
                </a-form-item>
                <a-form-item label="密碼" name="password">
                    <a-input-password
                        v-model:value="formState.password"
                        :placeholder="
                            modalMode === 'add' ? '請輸入密碼' : '留空則不變'
                        "
                    />
                </a-form-item>
                <a-form-item
                    v-if="modalMode === 'edit'"
                    label="連結前台會員"
                    extra="供中樞廳 SSO 使用；一位會員僅能綁定一位管理員"
                >
                    <a-select
                        v-model:value="formState.linked_user_id"
                        show-search
                        allow-clear
                        placeholder="搜尋會員姓名或 Email"
                        :filter-option="false"
                        :options="linkedUserOptions"
                        :loading="linkedUserSearchLoading"
                        @search="onLinkedUserSearch"
                    />
                </a-form-item>
            </template>
        </EditModal>

        <!-- 角色管理 Modal -->
        <RoleManagementModal
            v-model:open="roleModalVisible"
            :current-user="currentManager"
            :all-roles="allRoles"
            :selected-roles="selectedRoles"
            :confirm-loading="roleModalLoading"
            @ok="handleRoleModalOk"
            @cancel="handleRoleModalCancel"
        />

        <!-- 權限檢視 Modal -->
        <DetailModal
            v-model:open="permModalVisible"
            :title="
                permissionTarget ? `權限 - ${permissionTarget.name}` : '權限'
            "
            width="960px"
            :data="permissionTarget"
            :basic-info="permissionTargetInfo"
            :permissions="permissionList"
            :show-permissions="true"
        />

        <!-- 刪除管理者 Modal -->
        <EditModal
            v-model:open="deleteModalVisible"
            title="刪除管理者"
            :confirm-loading="deleteLoading"
            @ok="() => removeManager(deleteTarget)"
            @cancel="handleDeleteCancel"
        >
            <template #fields>
                <div>確認刪除管理者「{{ deleteTarget?.name }}」？</div>
            </template>
        </EditModal>
    </div>
</template>

<script setup>
// AdminList Component - 帳號管理頁面
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { message } from 'ant-design-vue';
import {
    showErrorMessage,
    handleValidationErrors,
    handleApiError
} from '@/utils/messageHelper';
import {
    getManagers,
    createManager,
    updateManager,
    deleteManager
} from '@/api/managers';
import { searchUsers } from '@/api/users';
import { getRoles } from '@/api/roles';
import { fetchAllOptionRows } from '@/utils/safePaginatedFetch';
import {
    getManagerPermissions,
    assignManagerRoles,
    removeManagerRole
} from '@/api/roles/managerPermissions';
import { useAdminInfo } from '@/stores/useAdminInfo';
import DataTable from '@/components/common/DataTable.vue';
import EditModal from '@/components/common/EditModal.vue';
import RoleManagementModal from '@/components/common/RoleManagementModal.vue';
import DetailModal from '@/components/common/DetailModal.vue';

// 使用者資料
const adminStore = useAdminInfo();
const route = useRoute();

// 資料狀態
const managers = ref([]);
const loading = ref(false);
// 移除頂部關鍵字搜尋，改為獨立的名稱/Email 篩選
const allRoles = ref([]);
// 以 id 快取名稱，避免在 template 多次 O(n) 搜尋
const roleIdToName = computed(() => {
    const map = {};
    (allRoles.value || []).forEach((r) => {
        const idNum = Number(r.id);
        if (Number.isFinite(idNum)) map[idNum] = r.name;
    });
    return map;
});
// 篩選條件
const filters = reactive({
    name: '',
    email: '',
    roleId: null
});

// 分頁設定
const pagination = reactive({
    current: 1,
    pageSize: 10,
    total: 0
});

// Modal 狀態
const modalVisible = ref(false);
const modalMode = ref('add');
const modalLoading = ref(false);
const currentManager = ref(null);

// 角色管理 Modal
const roleModalVisible = ref(false);
const roleModalLoading = ref(false);
// selectedRoleIds 現在由 RoleManagementModal 組件管理

// 權限檢視 Modal 狀態
const permModalVisible = ref(false);
const permissionTarget = ref(null);
const permissionList = ref([]);

// 計算屬性：權限檢視的基本資訊
const permissionTargetInfo = computed(() => {
    if (!permissionTarget.value) return [];
    return [
        { label: '名稱', value: permissionTarget.value.name },
        { label: 'Email', value: permissionTarget.value.email }
    ];
});

const canCreate = computed(() => adminStore.hasPermissionCode('990201')); // 新增管理者
const canEdit = computed(() => adminStore.hasPermissionCode('990202')); // 修改管理者
const canAssignRoles = computed(() => adminStore.hasPermissionCode('990205')); // 角色分配
const canDelete = computed(() => adminStore.hasPermissionCode('990203')); // 刪除管理者

// 刪除視窗狀態
const deleteModalVisible = ref(false);
const deleteLoading = ref(false);
const deleteTarget = ref(null);

// 將各種 API 回傳格式正規化為 [{ code: 'xxxxxx' }, ...]
const normalizeOwnedPermissions = (raw) => {
    const arr = Array.isArray(raw) ? raw : [];
    return arr
        .map((p) => {
            if (!p) return null;
            // 直接代碼
            if (typeof p === 'string' || typeof p === 'number')
                return { code: String(p) };
            // 物件各種欄位
            if (p.code) return { code: String(p.code) };
            if (p.permission_code) return { code: String(p.permission_code) };
            if (p.permission?.code) return { code: String(p.permission.code) };
            if (p.perm?.code) return { code: String(p.perm.code) };
            return null;
        })
        .filter(Boolean);
};

// 從回傳物件中嘗試抽取「擁有權限陣列」
const extractOwnedArray = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.permissions)) return data.permissions;
    if (Array.isArray(data?.data?.permissions)) return data.data.permissions;
    if (Array.isArray(data?.roles)) {
        return data.roles.flatMap((r) => r?.permissions || r?.perms || []);
    }
    if (Array.isArray(data?.data?.roles)) {
        return data.data.roles.flatMap((r) => r?.permissions || r?.perms || []);
    }
    return [];
};

const showPermissions = async (record) => {
    try {
        permModalVisible.value = true;
        permissionList.value = [];
        // 取得此管理員擁有的權限（各種可能的包裝層級都處理）
        const res = await getManagerPermissions(record.id);
        const data = res?.data || {};

        // 從 roles[].permissions 扁平化並去重（根據 code）
        const allPermissions = [];
        const codeSet = new Set();

        if (Array.isArray(data.roles)) {
            data.roles.forEach((role) => {
                if (Array.isArray(role.permissions)) {
                    role.permissions.forEach((perm) => {
                        const code = perm.code || String(perm.id);
                        if (!codeSet.has(code)) {
                            codeSet.add(code);
                            allPermissions.push({
                                id: perm.id,
                                code: code,
                                name: perm.name
                            });
                        }
                    });
                }
            });
        }

        // 如果沒有從 roles 取得，嘗試其他方式
        if (allPermissions.length === 0) {
            const ownedRaw = extractOwnedArray(data);
            const owned = normalizeOwnedPermissions(ownedRaw);
            permissionList.value = owned;
        } else {
            permissionList.value = allPermissions;
        }

        permissionTarget.value = {
            ...record,
            permissions: permissionList.value
        };
    } catch (e) {
        console.error('載入管理員權限失敗:', e);
        // 即使失敗也打開空清單，避免整個流程卡住
        permissionList.value = [];
        permissionTarget.value = { ...record, permissions: [] };
    }
};

// 計算屬性：當前管理員的角色
const selectedRoles = computed(() => {
    return currentManager.value?.roles || [];
});

// 表單資料
const formState = reactive({
    name: '',
    email: '',
    password: '',
    linked_user_id: undefined
});

const linkedUserOptions = ref([]);
const linkedUserSearchLoading = ref(false);

const onLinkedUserSearch = async (keyword) => {
    const q = String(keyword || '').trim();
    if (q.length < 1) return;
    linkedUserSearchLoading.value = true;
    try {
        const res = await searchUsers({ name: q, limit: 20 });
        const rows = res.data?.data || res.data || [];
        linkedUserOptions.value = (Array.isArray(rows) ? rows : []).map((u) => ({
            value: u.id,
            label: `${u.name || '—'} · ${u.email || u.phone || u.id}`
        }));
    } catch (e) {
        console.error(e);
    } finally {
        linkedUserSearchLoading.value = false;
    }
};

// 表單驗證規則
const formRules = {
    name: [{ required: true, message: '請輸入管理員名稱', trigger: 'blur' }],
    email: [
        { required: true, message: '請輸入 Email', trigger: 'blur' },
        { type: 'email', message: 'Email 格式不正確', trigger: 'blur' }
    ],
    password: [
        { required: true, message: '請輸入密碼', trigger: 'blur' },
        { min: 6, message: '密碼長度至少 6 位', trigger: 'blur' }
    ]
};

// 編輯用規則：密碼可留空（不變更）
const formRulesEdit = {
    name: [{ required: true, message: '請輸入管理員名稱', trigger: 'blur' }],
    email: [
        { required: true, message: '請輸入 Email', trigger: 'blur' },
        { type: 'email', message: 'Email 格式不正確', trigger: 'blur' }
    ],
    password: [{ min: 6, message: '密碼長度至少 6 位', trigger: 'blur' }]
};

// 表格欄位配置
const columns = [
    {
        title: '名稱',
        key: 'name',
        ellipsis: true,
        width: 150,
        fixed: 'left'
    },
    {
        title: 'Email',
        key: 'email',
        width: 200,
        ellipsis: true
    },
    {
        title: '角色',
        key: 'roles',
        width: 180,
        ellipsis: true
    },
    {
        title: '狀態',
        key: 'status',
        width: 100,
        align: 'center'
    },
    {
        title: '建立時間',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 180
    },
    {
        title: '操作',
        key: 'action',
        width: 280,
        align: 'center'
    }
];

// 載入管理員列表
const loadManagers = async () => {
    try {
        loading.value = true;
        const response = await getManagers({
            page: pagination.current,
            per_page: pagination.pageSize,
            // 依需求帶入後端可接受的查詢鍵名
            name: filters.name || undefined,
            email: filters.email || undefined,
            role: filters.roleId || undefined
        });

        if (response.result) {
            // API 回應格式：response.data.data 是管理員陣列
            managers.value = response.data.data || [];

            // 更新分頁資訊（支援 data.pagination 或 data 直屬欄位）
            const pag = response.data.pagination;
            if (pag) {
                pagination.total = pag.total ?? 0;
                pagination.current = pag.current_page ?? 1;
                if (pag.per_page != null) pagination.pageSize = pag.per_page;
            } else {
                pagination.total = response.data.total || 0;
                pagination.current = response.data.current_page || 1;
            }
        }
    } catch (error) {
        console.error('載入管理員列表失敗:', error);
        showErrorMessage('載入管理員列表失敗');
    } finally {
        loading.value = false;
    }
};

// 正規化角色資料 → [{ id, name }]
const normalizeRoles = (list) => {
    const toNumIfNumeric = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : v;
    };
    return (Array.isArray(list) ? list : [])
        .map((r) => ({
            id: toNumIfNumeric(r?.id ?? r?.role_id ?? r?.value),
            name:
                r?.name ??
                r?.role_name ??
                r?.label ??
                String(r?.id ?? r?.role_id ?? r ?? '')
        }))
        .filter((r) => r.id != null && r.name != null);
};

// 載入所有角色（分頁翻完）
const loadAllRoles = async () => {
    try {
        const candidates = await fetchAllOptionRows((params) => getRoles(params));
        allRoles.value = normalizeRoles(candidates);
    } catch (error) {
        console.error('載入角色列表失敗:', error);
        allRoles.value = [];
    }
};

// 解析 record.roles：可能是物件陣列、id 陣列，甚至混合
const resolveRoles = (record) => {
    const roles = Array.isArray(record.roles) ? record.roles : [];
    return roles;
};

// 顯示角色標籤：若為 id，對照 allRoles 取得 name；否則顯示 role.name
const getRoleLabel = (role) => {
    if (role && typeof role === 'object' && role.name) return role.name;
    const id = typeof role === 'object' ? role.id : Number(role);
    const name = roleIdToName.value[Number(id)];
    return name != null ? name : String(role);
};

// 搜尋處理
const handleSearch = () => {
    pagination.current = 1;
    loadManagers();
};

// 重置篩選條件
const resetFilters = () => {
    filters.name = '';
    filters.email = '';
    filters.roleId = null;
    pagination.current = 1;
    loadManagers();
};

// 表格變更處理
const handleTableChange = ({ pagination: paginationInfo }) => {
    pagination.current = paginationInfo.current;
    pagination.pageSize = paginationInfo.pageSize;
    loadManagers();
};

// 顯示新增 Modal
const showAddModal = () => {
    modalMode.value = 'add';
    modalVisible.value = true;
    resetForm();
};

// 編輯管理員
const editManager = (record) => {
    modalMode.value = 'edit';
    modalVisible.value = true;
    currentManager.value = record;
    formState.name = record.name;
    formState.email = record.email;
    formState.password = '';
    formState.linked_user_id = record.linked_user_id ?? undefined;
    linkedUserOptions.value = record.linked_user
        ? [
              {
                  value: record.linked_user.id,
                  label: `${record.linked_user.name} · ${record.linked_user.email}`
              }
          ]
        : record.linked_user_id
          ? [{ value: record.linked_user_id, label: `#${record.linked_user_id}` }]
          : [];
};

// 重置表單
const resetForm = () => {
    formState.name = '';
    formState.email = '';
    formState.password = '';
    formState.linked_user_id = undefined;
    linkedUserOptions.value = [];
    currentManager.value = null;
};

// Modal 確認處理
const handleModalOk = async () => {
    try {
        modalLoading.value = true;

        if (modalMode.value === 'add') {
            // 新增管理員
            const response = await createManager({
                name: formState.name,
                email: formState.email,
                password: formState.password
            });
            if (response.result) {
                message.success('新增成功');
                modalVisible.value = false;
                loadManagers();
            } else {
                // 處理驗證錯誤
                const errorMessage = handleValidationErrors(response, {
                    name: '名稱',
                    email: 'Email',
                    password: '密碼'
                });
                showErrorMessage(errorMessage);
            }
        } else {
            // 編輯管理員
            const payload = {
                name: formState.name,
                linked_user_id: formState.linked_user_id ?? null
            };
            if (formState.password && formState.password.length >= 6) {
                payload.password = formState.password;
            }
            const response = await updateManager(
                currentManager.value.id,
                payload
            );
            if (response.result) {
                message.success('更新成功');
                modalVisible.value = false;
                loadManagers();
            } else {
                // 處理驗證錯誤
                const errorMessage = handleValidationErrors(response, {
                    name: '名稱',
                    email: 'Email',
                    password: '密碼'
                });
                showErrorMessage(errorMessage);
            }
        }
    } catch (error) {
        console.error('操作失敗:', error);
        // 檢查 catch 中的錯誤是否包含 errors 物件
        const errorData = error?.response?.data || error?.data || error;
        if (errorData?.errors) {
            const errorMessage = handleValidationErrors(errorData, {
                name: '名稱',
                email: 'Email',
                password: '密碼'
            });
            showErrorMessage(errorMessage);
        } else {
            showErrorMessage('操作失敗');
        }
    } finally {
        modalLoading.value = false;
    }
};

// Modal 取消處理
const handleModalCancel = () => {
    resetForm();
};

// 管理角色
const manageRoles = async (record) => {
    currentManager.value = record;
    roleModalVisible.value = true;
    roleModalLoading.value = true;

    try {
        // 獲取管理員目前的權限
        const response = await getManagerPermissions(record.id);
        if (response.result && response.data.roles) {
            // 角色資料會傳遞給 RoleManagementModal 組件
        }
    } catch (error) {
        console.error('載入權限失敗:', error);
        showErrorMessage('載入權限失敗');
    } finally {
        roleModalLoading.value = false;
    }
};

// 角色管理確認
const handleRoleModalOk = async (selectedRoleIds) => {
    try {
        roleModalLoading.value = true;

        // 以「完整集合」提交，確保同時新增與保留既有角色
        const response = await assignManagerRoles(
            currentManager.value.id,
            selectedRoleIds
        );
        if (!response.result) {
            throw new Error(response.message || '分配角色失敗');
        }

        message.success('角色更新成功');
        roleModalVisible.value = false;
        loadManagers();
    } catch (error) {
        console.error('更新角色失敗:', error);
        // 使用統一的錯誤處理函數
        handleApiError(error, '更新角色失敗', {
            role_id: '角色'
        });
    } finally {
        roleModalLoading.value = false;
    }
};

// 角色管理取消
const handleRoleModalCancel = () => {
    currentManager.value = null;
};

// 判斷狀態是否為啟用
const isActiveStatus = (status) => status === true || status === 'active';

// 直接從 switch 更新狀態（checked 為 boolean）
const updateStatus = async (record, checked) => {
    try {
        const response = await updateManager(record.id, { status: !!checked });
        if (response.result) {
            message.success(`已${checked ? '啟用' : '停用'}`);
            // 即時更新前端資料，避免整表刷新感覺慢
            managers.value = managers.value.map((m) =>
                m.id === record.id ? { ...m, status: checked } : m
            );
        } else {
            showErrorMessage(response.message || '狀態更新失敗');
        }
    } catch (error) {
        console.error('狀態更新失敗:', error);
        showErrorMessage('狀態更新失敗');
    }
};

// 刪除管理者
const removeManager = async (record) => {
    try {
        deleteLoading.value = true;
        const res = await deleteManager(record.id);
        if (res?.result) {
            message.success('已刪除管理者');
            loadManagers();
            deleteModalVisible.value = false;
            deleteTarget.value = null;
        } else {
            showErrorMessage(res?.message || '刪除失敗');
        }
    } catch (e) {
        console.error('刪除管理者失敗:', e);
        showErrorMessage('刪除管理者失敗');
    } finally {
        deleteLoading.value = false;
    }
};

// 開啟刪除管理者視窗
const openDeleteManager = (record) => {
    deleteTarget.value = record;
    deleteModalVisible.value = true;
};

// 刪除 Modal 取消處理
const handleDeleteCancel = () => {
    deleteTarget.value = null;
};

// 初始化
onMounted(() => {
    // 從 URL query 初始化角色篩選（來自 RoleList 點擊）
    const qRoleId =
        route.query.role || route.query.role_id || route.query.roleId;
    if (qRoleId) {
        filters.roleId = Number(qRoleId);
        pagination.current = 1;
        // 先載入角色清單以便顯示名稱，再載入列表
        loadAllRoles();
        loadManagers();
        return;
    }
    loadAllRoles();
    loadManagers();
});
</script>

<style scoped>
.page {
    padding: 24px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    min-height: 100vh;
}

.page-header {
    margin-bottom: 24px;
}

.page-header h1 {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 8px 0;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.page-header p {
    margin: 0;
    color: #6a6a7a;
    font-size: 14px;
    font-family: 'Courier New', monospace;
}

.content {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border: 2px solid transparent;
    border-radius: 16px;
    padding: 32px;
    position: relative;
    box-shadow: 0 0 30px rgba(42, 74, 122, 0.3);
}

.content::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
}

.toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;
    position: relative;
    z-index: 1;
}

.search-box {
    flex: 1;
    max-width: 400px;
}

/* Filter 輸入框統一樣式 */
.filters :deep(.ant-input-wrapper) {
    position: relative;
}

.filters :deep(.ant-input-wrapper::before) {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 12px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
    pointer-events: none;
    z-index: 0;
}

.filters :deep(.ant-input) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 8px !important;
    box-shadow: 0 0 10px rgba(42, 74, 122, 0.1) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    height: 32px !important;
    padding: 0 12px !important;
    position: relative;
    z-index: 1;
    width: 300px;
    transition: all 0.3s ease !important;
}

.filters :deep(.ant-input::placeholder) {
    color: rgba(42, 42, 58, 0.5) !important;
    opacity: 1 !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
}

.filters :deep(.ant-input:hover),
.filters :deep(.ant-input:focus) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid rgba(200, 200, 210, 0.4) !important;
    box-shadow: 0 0 15px rgba(42, 74, 122, 0.2) !important;
}

.filters :deep(.ant-input:focus) {
    border-color: rgba(42, 74, 122, 0.6) !important;
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.3) !important;
}

/* Filter 輸入框統一樣式 */
.filters :deep(.filter-input) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 8px !important;
    box-shadow: 0 0 10px rgba(42, 74, 122, 0.1) !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    height: 32px !important;
    padding: 0 12px !important;
    position: relative;
    z-index: 1;
    width: 300px;
    transition: all 0.3s ease !important;
}

.filters :deep(.filter-input::placeholder) {
    color: rgba(42, 42, 58, 0.5) !important;
    opacity: 1 !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
}

.filters :deep(.filter-input:hover) {
    border-color: rgba(42, 74, 122, 0.6) !important;
    box-shadow: 0 0 15px rgba(42, 74, 122, 0.2) !important;
}

.filters :deep(.filter-input:focus) {
    border-color: #2a4a7a !important;
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.3) !important;
}

/* 選擇器樣式 - 與商品分類頁面一致 */
.filters :deep(.status-select .ant-select) {
    border-radius: 8px !important;
}

.filters :deep(.status-select .ant-select-selector) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 8px !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    height: 32px !important;
    transition: all 0.3s ease !important;
    box-shadow: 0 0 10px rgba(42, 74, 122, 0.1) !important;
}

.filters :deep(.status-select .ant-select-selector:hover) {
    border-color: rgba(42, 74, 122, 0.6) !important;
    box-shadow: 0 0 15px rgba(42, 74, 122, 0.2) !important;
}

.filters :deep(.status-select .ant-select-focused .ant-select-selector) {
    border-color: #2a4a7a !important;
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.3) !important;
}

.filters :deep(.status-select .ant-select-selection-item) {
    line-height: 30px !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
}

.filters :deep(.status-select .ant-select-selection-placeholder) {
    line-height: 30px !important;
    font-size: 13px !important;
    color: rgba(42, 42, 58, 0.5) !important;
    font-family: 'Courier New', monospace !important;
}

.filters :deep(.status-select .ant-select-arrow) {
    color: rgba(42, 42, 58, 0.6) !important;
    transition: color 0.3s ease !important;
}

.filters :deep(.status-select .ant-select:hover .ant-select-arrow) {
    color: #2a4a7a !important;
}

/* 下拉選單樣式 */
.filters :deep(.status-select .ant-select-dropdown) {
    background: rgba(255, 255, 255, 0.98) !important;
    backdrop-filter: blur(10px) !important;
    border: 1.5px solid rgba(42, 74, 122, 0.3) !important;
    border-radius: 12px !important;
    box-shadow: 0 0 25px rgba(42, 74, 122, 0.25) !important;
    padding: 8px !important;
    margin-top: 4px !important;
}

.filters :deep(.status-select .ant-select-item) {
    background: transparent !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    border-radius: 8px !important;
    margin: 2px 0 !important;
    padding: 8px 12px !important;
    transition: all 0.2s ease !important;
}

.filters :deep(.status-select .ant-select-item:hover) {
    background: rgba(42, 74, 122, 0.15) !important;
    color: #2a4a7a !important;
}

.filters :deep(.status-select .ant-select-item-option-selected) {
    background: rgba(42, 74, 122, 0.25) !important;
    color: #2a4a7a !important;
    font-weight: 600 !important;
}

.filters :deep(.status-select .ant-select-item-option-active) {
    background: rgba(42, 74, 122, 0.15) !important;
}

.admin-table {
    position: relative;
    z-index: 1;
}

.user-info {
    display: flex;
    flex-direction: column;
}

.user-name {
    font-weight: 500;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.user-email {
    font-size: 12px;
    color: #6a6a7a;
    font-family: 'Courier New', monospace;
}

.email-text {
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    font-size: 14px;
}

/* DataTable vaporwave theme integration */
:deep(.ant-card) {
    background: transparent;
    border: none;
}

:deep(.ant-card-body) {
    padding: 0;
}

/* 表格樣式已移動到 DataTable 組件中 */

:deep(.ant-btn-primary) {
    background: linear-gradient(45deg, #2a4a7a, #3b6ea5);
    border: none;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    font-weight: 600;
}

:deep(.ant-btn-primary:hover) {
    background: linear-gradient(45deg, #3b6ea5, #2a4a7a);
    box-shadow: 0 0 15px rgba(42, 74, 122, 0.5);
}

:deep(
    .ant-btn:not(.unified-search-btn):not(.unified-reset-btn):not(
            [class*='action-btn-']
        )
) {
    font-family: 'Courier New', monospace;
    border-radius: 8px;
}

/* 確保查詢和重置按鈕樣式優先 - 與角色管理一致，放在最前面 */
.filters .unified-search-btn,
.filters .unified-search-btn.ant-btn,
.filters .unified-search-btn.ant-btn-default,
.filters :deep(.unified-search-btn),
.filters :deep(.unified-search-btn.ant-btn),
.filters :deep(.unified-search-btn.ant-btn-default) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid #7a9ba5 !important;
    color: #7a9ba5 !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 500 !important;
    border-radius: 8px !important;
    transition: all 0.3s ease !important;
}

.filters .unified-search-btn:hover,
.filters .unified-search-btn.ant-btn:hover,
.filters .unified-search-btn.ant-btn-default:hover,
.filters :deep(.unified-search-btn:hover),
.filters :deep(.unified-search-btn.ant-btn:hover),
.filters :deep(.unified-search-btn.ant-btn-default:hover) {
    background: rgba(122, 155, 165, 0.1) !important;
    border-color: #7a9ba5 !important;
    color: #7a9ba5 !important;
    box-shadow: 0 2px 8px rgba(122, 155, 165, 0.3) !important;
}

.filters .unified-reset-btn,
.filters .unified-reset-btn.ant-btn,
.filters .unified-reset-btn.ant-btn-default,
.filters :deep(.unified-reset-btn),
.filters :deep(.unified-reset-btn.ant-btn),
.filters :deep(.unified-reset-btn.ant-btn-default) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid #c4a5a5 !important;
    color: #c4a5a5 !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 500 !important;
    border-radius: 8px !important;
    transition: all 0.3s ease !important;
}

.filters .unified-reset-btn:hover,
.filters .unified-reset-btn.ant-btn:hover,
.filters .unified-reset-btn.ant-btn-default:hover,
.filters :deep(.unified-reset-btn:hover),
.filters :deep(.unified-reset-btn.ant-btn:hover),
.filters :deep(.unified-reset-btn.ant-btn-default:hover) {
    background: rgba(196, 165, 165, 0.1) !important;
    border-color: #c4a5a5 !important;
    color: #c4a5a5 !important;
    box-shadow: 0 2px 8px rgba(196, 165, 165, 0.3) !important;
}

:deep(
    .ant-btn-default:not(.unified-search-btn):not(.unified-reset-btn):not(
            [class*='action-btn-']
        )
) {
    background: rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(168, 255, 246, 0.5);
    color: #2a2a3a;
}

:deep(
    .ant-btn-default:not(.unified-search-btn):not(.unified-reset-btn):not(
            [class*='action-btn-']
        ):hover
) {
    background: rgba(42, 74, 122, 0.1);
    border-color: rgba(200, 200, 210, 0.4);
    color: #2a2a3a;
}

/* 標籤樣式已移動到 DataTable 組件中 */

.status-text {
    font-family: 'Courier New', monospace;
    font-weight: 500;
}

.status-text.active {
    color: #4a7c59;
    /* 深綠色 - 啟用 */
}

/* 狀態切換按鈕風格：明確呈現目前狀態 */
.status-toggle-btn {
    border-color: rgba(42, 74, 122, 0.35);
}

/* 還原為預設 a-switch 樣式，移除客製化 */
.antdv-switch-clean :deep(.ant-switch) {
    border-radius: 9999px !important;
    /* pill，避免被全域樣式改成方的 */
}

.antdv-switch-clean :deep(.ant-switch-handle) {
    border-radius: 9999px !important;
    /* knob 維持圓形 */
}

/* 權限圓形圖示按鈕樣式（有圈圈的框） */
.role-tag {
    cursor: pointer;
}

.role-tag:hover {
    box-shadow: 0 0 10px rgba(138, 175, 255, 0.35);
}

.status-toggle-btn :deep(.ant-btn-icon) {
    display: inline-flex;
    align-items: center;
}

:deep(.ant-modal-content) {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border: 2px solid transparent;
    border-radius: 16px;
}

:deep(.ant-modal-content::before) {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
}

:deep(.ant-modal-header) {
    background: transparent;
    border-bottom: 1px solid rgba(42, 74, 122, 0.3);
}

:deep(.ant-modal-title) {
    color: #64748b;
    font-family: 'Courier New', monospace;
    font-weight: 600;
}

:deep(.ant-modal-body) {
    color: #2a2a3a;
}

:deep(.ant-form-item-label > label) {
    color: #0e7490;
    font-family: 'Courier New', monospace;
    font-weight: 500;
}

:deep(.ant-checkbox-wrapper) {
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

:deep(.ant-checkbox-checked .ant-checkbox-inner) {
    background-color: #2a4a7a;
    border-color: #2a4a7a;
}

:deep(.ant-divider) {
    border-color: rgba(42, 74, 122, 0.3);
}

:deep(.ant-descriptions-item-label) {
    background: rgba(255, 255, 255, 0.8);
    color: #0e7490;
    font-family: 'Courier New', monospace;
}

:deep(.ant-descriptions-item-content) {
    background: rgba(255, 255, 255, 0.6);
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

:deep(.ant-pagination-total-text) {
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

/* 確保所有表格內容文字都是深色 */
:deep(.ant-table-tbody tr td) {
    color: #2a2a3a !important;
}

:deep(.ant-table-tbody tr td *) {
    color: #2a2a3a !important;
}

/* 縮小操作按鈕 - 覆蓋 DataTable 的樣式 */
.action-buttons :deep(.ant-space-item) {
    margin-right: 4px !important;
}
</style>
